import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createTrustedHTML } from '../../../lib/trustedTypes';

export default function TradeInFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'How do I know if I\'m getting a fair trade-in value?',
      answer: 'Research your vehicle\'s value on Kelley Blue Book (KBB) and NADA before getting appraisals. Compare multiple offers from different dealers and private buyers. Our calculator gives you an instant estimate, and our official appraisal is based on real market data — not what maximizes our profit. We show you how we calculated your offer, so you can see it\'s fair.'
    },
    {
      question: 'Should I trade in or sell my car privately?',
      answer: 'Private sales typically net 10-15% more than trade-ins, but require significant time and effort: listing ads, meeting strangers, handling paperwork, and dealing with lowball offers. Trade-ins are convenient, safe, and instant — you drive away in your new vehicle the same day. If time is worth money to you, trading in makes sense. If you have weeks to spare and want maximum value, sell privately.'
    },
    {
      question: 'What if I still owe money on my current vehicle?',
      answer: 'No problem — this is called having a loan payoff. If your trade-in value is higher than what you owe, the difference goes toward your new vehicle as a down payment (positive equity). If you owe more than the vehicle is worth (negative equity), that amount can be rolled into your new loan, though we recommend paying down the difference first if possible to avoid being underwater on your new loan.'
    },
    {
      question: 'Do I need to fix damage before trading in?',
      answer: 'It depends on the severity. Minor cosmetic issues (small scratches, door dings) usually aren\'t worth fixing — the repair cost often exceeds the value increase. However, mechanical issues, check engine lights, or significant body damage should be addressed if the repair cost is less than the value loss. Our appraisers will assess everything during inspection and explain how it affects your offer.'
    },
    {
      question: 'How long does the trade-in appraisal process take?',
      answer: 'Our online appraisal takes 2 minutes to submit. You\'ll receive an initial offer within 24 hours. If you accept, we schedule a physical inspection (15-30 minutes) to verify condition and finalize the offer. The entire process from submission to driving away in your new vehicle can be completed in 2-3 days. No waiting weeks like at traditional dealerships.'
    },
    {
      question: 'Can I trade in a vehicle that doesn\'t run?',
      answer: 'Yes, we accept non-running vehicles, but the value will reflect the mechanical issues and cost of repairs. Be upfront about the problems — we\'ll still make an offer based on parts value, scrap value, or repair potential. Honesty helps us give you the best possible offer for a non-running vehicle.'
    },
    {
      question: 'What documents do I need to trade in my vehicle?',
      answer: 'You\'ll need: (1) Vehicle title (or payoff information if you have a loan), (2) Valid driver\'s license, (3) Current registration, (4) All keys and remotes, (5) Service records (optional but helpful), (6) Owner\'s manual (if available). If you\'re still making payments, we handle the payoff directly with your lender.'
    },
    {
      question: 'Will my trade-in value change after the inspection?',
      answer: 'Our initial offer is based on the information you provide. If the vehicle\'s condition matches your description, the offer stands. However, if we discover undisclosed damage, mechanical issues, or odometer discrepancies during inspection, we may adjust the offer. This is why honesty upfront is crucial — it saves everyone time and ensures a smooth process.'
    },
    {
      question: 'Do you accept trade-ins if I\'m not buying from you?',
      answer: 'Yes! While most customers trade in when purchasing a vehicle from our inventory, we also buy vehicles outright. If you just want to sell your car without buying, check out our <a href="/sell-your-car" class="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">Sell Your Car</a> page for cash offers. Same fair pricing, just a different process.'
    },
    {
      question: 'How is your trade-in offer different from Astoria Ford or Warrenton Kia?',
      answer: 'Traditional dealerships lowball trade-ins to maximize profit margins on both your trade and their sale. They use your trade-in as a negotiation tool, playing games with numbers. We\'re an auto broker, not a traditional dealer — we don\'t have the same profit pressures. Our business model is built on transparency and fair pricing, so we offer market value for your trade-in. Our customers consistently report getting $3,000-$5,000 more than traditional dealer offers.'
    },
    {
      question: 'Does trading in my car reduce the sales tax I owe in Oregon?',
      answer: 'Oregon has no sales tax on used vehicle purchases — so there\'s no sales tax offset to calculate at the point of sale. You\'re not paying sales tax to begin with, so there\'s nothing to reduce. What you do pay in Oregon: a title fee (~$116), registration fees ($86–$152 for 2 years), and a doc fee capped at $150. That\'s it. If you\'re a Washington resident registering the vehicle at your WA DOL, your trade-in value does reduce the taxable purchase price for Washington sales tax purposes — which can save you a meaningful amount. We\'ll walk you through the full number for your specific situation before you sign anything.'
    }
  ];

  return (
    <section className="mb-16">
      <div className="text-center mb-12">
        <span className="text-xs uppercase tracking-wider text-[#92650a] font-medium">Common Questions</span>
        <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-3xl font-bold text-[#1A2332] mt-2 mb-3">
          Trade-In Value FAQ
        </h2>
        <p className="text-sm text-[#4A5568] max-w-2xl mx-auto">
          Everything you need to know about getting the best trade-in value for your vehicle.
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          const btnId = `tradein-faq-btn-${index}`;
          const panelId = `tradein-faq-panel-${index}`;

          return (
            <div
              key={index}
              className="bg-white rounded-xl border border-[#E8E2D6] overflow-hidden hover:shadow-md transition-shadow"
            >
              <button
                id={btnId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#F7F5F0] transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-[#1A2332] focus-visible:ring-offset-1"
              >
                <h3 className="text-base font-semibold text-[#1A2332] pr-4">
                  {faq.question}
                </h3>
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${isOpen ? 'bg-[#E8B84B]' : 'bg-[#E8B84B]/10'}`}>
                  <i className={`text-xl ${isOpen ? 'ri-subtract-line text-white' : 'ri-add-line text-[#E8B84B]'}`}></i>
                </div>
              </button>
              {isOpen && (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  className="px-6 pb-5"
                >
                  <p
                    className="text-sm text-[#4A5568] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: createTrustedHTML(faq.answer) }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Still Have Questions CTA */}
      <div className="mt-12 bg-gradient-to-br from-[#E8B84B]/10 to-white rounded-2xl p-8 border border-[#E8B84B]/20 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-[#E8B84B] rounded-2xl mx-auto mb-4">
          <i className="ri-question-answer-line text-white text-2xl"></i>
        </div>
        <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-xl font-bold text-[#1A2332] mb-2">Still Have Questions?</h3>
        <p className="text-sm text-[#4A5568] mb-6 max-w-md mx-auto">
          Our team is here to help. Call us at <a href="tel:8554686348" className="text-[#92650a] hover:text-[#b45309] font-semibold cursor-pointer">(855) 468-6348</a> or visit our <Link to="/contact" className="text-[#92650a] hover:text-[#b45309] font-semibold cursor-pointer">contact page</Link>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
          <a
            href="tel:8554686348"
            className="inline-flex items-center justify-center px-6 py-3 bg-[#E8B84B] text-white rounded-xl text-sm font-semibold hover:bg-[#D4A43A] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Call Us Now
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#1A2332] border-2 border-[#E8E2D6] rounded-xl text-sm font-semibold hover:border-[#E8B84B] hover:bg-[#F7F5F0] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line mr-2"></i>
            Send a Message
          </Link>
        </div>

        {/* Related page links */}
        <div className="border-t border-[#E8B84B]/20 pt-6">
          <p className="text-xs text-[#4A5568] mb-3 font-medium uppercase tracking-wide">Related Services</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/sell-your-car"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E8E2D6] rounded-full text-xs font-medium text-[#1A2332] hover:border-[#E8B84B] hover:text-[#E8B84B] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-[#E8B84B]"></i>
              Sell Your Car for Cash
            </Link>
            <Link
              to="/inventory"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E8E2D6] rounded-full text-xs font-medium text-[#1A2332] hover:border-[#E8B84B] hover:text-[#E8B84B] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-car-line text-[#E8B84B]"></i>
              Browse Inventory
            </Link>
            <Link
              to="/financing"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E8E2D6] rounded-full text-xs font-medium text-[#1A2332] hover:border-[#E8B84B] hover:text-[#E8B84B] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bank-card-line text-[#E8B84B]"></i>
              Financing Options
            </Link>
            <Link
              to="/broker-services"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E8E2D6] rounded-full text-xs font-medium text-[#1A2332] hover:border-[#E8B84B] hover:text-[#E8B84B] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-service-line text-[#E8B84B]"></i>
              Broker Services
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}